import { GameObjects, Scene } from 'phaser';
import { EnemyType, UnitStats } from '../config/BalanceConfig';
import { COLORS, SIZES, UNIT_STATS } from '../config/Constants';

export class Enemy extends GameObjects.GameObject {
  public body: any;
  public graphics: GameObjects.Shape;
  private enemyType: EnemyType;
  private stats: UnitStats;
  private lastAttackTime: number = 0;

  constructor(scene: Scene, x: number, y: number, type: EnemyType) {
    super(scene, 'Enemy');
    
    this.enemyType = type;
    this.stats = this.getInitialStats(type);
    
    this.graphics = this.createEnemyGraphic(x, y, type);
    scene.physics.add.existing(this.graphics);
    this.body = this.graphics.body;
    
    // Store position for collision detection
    this.graphics.setPosition(x, y);
    
    scene.add.existing(this);
  }

  public get x(): number {
    return this.graphics?.x || 0;
  }

  public get y(): number {
    return this.graphics?.y || 0;
  }

  public set x(value: number) {
    if (this.graphics) this.graphics.x = value;
  }

  public set y(value: number) {
    if (this.graphics) this.graphics.y = value;
  }

  private getInitialStats(type: EnemyType): UnitStats {
    switch (type) {
      case EnemyType.MELEE:
        return {
          health: UNIT_STATS.ENEMY_MELEE.HEALTH,
          maxHealth: UNIT_STATS.ENEMY_MELEE.HEALTH,
          damage: UNIT_STATS.ENEMY_MELEE.DAMAGE,
          speed: UNIT_STATS.ENEMY_MELEE.SPEED,
          range: UNIT_STATS.ENEMY_MELEE.RANGE // Minimal range for melee
        };
      case EnemyType.RANGED:
        return {
          health: UNIT_STATS.ENEMY_RANGED.HEALTH,
          maxHealth: UNIT_STATS.ENEMY_RANGED.HEALTH,
          damage: UNIT_STATS.ENEMY_RANGED.DAMAGE,
          speed: UNIT_STATS.ENEMY_RANGED.SPEED,
          range: SIZES.ENEMY_RANGED_RADIUS * 8 // 8 times unit image radius for better ranged combat
        };
      default:
        return {
          health: UNIT_STATS.ENEMY_MELEE.HEALTH,
          maxHealth: UNIT_STATS.ENEMY_MELEE.HEALTH,
          damage: UNIT_STATS.ENEMY_MELEE.DAMAGE,
          speed: UNIT_STATS.ENEMY_MELEE.SPEED,
          range: UNIT_STATS.ENEMY_MELEE.RANGE
        };
    }
  }

  private createEnemyGraphic(x: number, y: number, type: EnemyType): GameObjects.Shape {
    switch (type) {
      case EnemyType.MELEE:
        return this.scene.add.rectangle(x, y, SIZES.ENEMY_MELEE_WIDTH, SIZES.ENEMY_MELEE_HEIGHT, COLORS.ENEMY_MELEE);
      case EnemyType.RANGED:
        return this.scene.add.circle(x, y, SIZES.ENEMY_RANGED_RADIUS, COLORS.ENEMY_RANGED);
      default:
        return this.scene.add.rectangle(x, y, SIZES.ENEMY_DEFAULT_SIZE, SIZES.ENEMY_DEFAULT_SIZE, COLORS.ENEMY_DEFAULT);
    }
  }

  public update(): void {
    // Movement logic will be handled by systems
  }

  public takeDamage(damage: number): void {
    this.stats.health -= damage;
    
    if (this.stats.health <= 0) {
      this.destroy();
    }
  }

  public getStats(): UnitStats {
    return { ...this.stats };
  }

  public getEnemyType(): EnemyType {
    return this.enemyType;
  }

  public getLastAttackTime(): number {
    return this.lastAttackTime;
  }

  public setLastAttackTime(time: number): void {
    this.lastAttackTime = time;
  }

  public canAttack(cooldown: number): boolean {
    return Date.now() - this.lastAttackTime >= cooldown;
  }

  public destroy(): void {
    this.graphics.destroy();
    super.destroy();
  }
}